import {Component, OnInit} from '@angular/core';
import {ActivatedRoute} from '@angular/router';
import {ScriptLoaderService} from 'angular-google-charts';
import {Subscription} from 'rxjs';
import {DemoService} from 'src/app/services/demo.service';
import {environment} from 'src/environments/environment';
import {animate, state, style, transition, trigger} from '@angular/animations';

@Component({
  selector: 'app-decision-table-dashboard',
  templateUrl: './decision-table-dashboard.component.html',
  styleUrls: ['./decision-table-dashboard.component.scss'],
  animations: [
    trigger('detailExpand', [
      state('collapsed', style({height: '0px', minHeight: '0'})),
      state('expanded', style({height: '*'})),
      transition('expanded <=> collapsed', animate('0ms cubic-bezier(0.4, 0.0, 0.2, 1)')),
    ]),
  ],
})
export class DecisionTableDashboardComponent implements OnInit {

  constructor(private demoService: DemoService, private activeRoute: ActivatedRoute, private loaderService: ScriptLoaderService) {
  }

  private _activeRouteSubscription: Subscription;

  id: any;
  virtualAssistantStatus = null;
  virtualAssistantResponse = '<div><h2>Analisi Dettagliata dell\'Andamento del Caso Clinico NMOSD</h2><p>Il grafico analizza il rischio di ricaduta per un caso di NMOSD (Neuromyelitis Optica Spectrum Disorder) in relazione a cinque strategie terapeutiche distinte. Ogni fase di trattamento presenta variazioni specifiche che richiedono un esame dettagliato per ottimizzare gli interventi clinici.</p><ul><li><strong>Prima Terapia: Rituximab</strong><br>Il trattamento iniziale con Rituximab si mostra altamente efficace, riducendo il rischio di ricaduta da circa l\'80% al 10% entro i primi 30 giorni. Questo indica una risposta inizialmente positiva al farmaco, suggerendo una forte azione immunomodulante.</li><li><strong>Seconda Terapia: Azathioprine</strong><br>A partire dal giorno 45, si osserva l\'introduzione di Azathioprine, accompagnata da un lieve ma significativo incremento del rischio. Questo potrebbe essere correlato a un adattamento del sistema immunitario o a una variazione nell\'efficacia del farmaco stesso.</li><li><strong>Terza Terapia: Mycophenolate Mofetil</strong><br>Nei giorni successivi (60-90), il rischio cresce momentaneamente, suggerendo una possibile inefficacia iniziale o una risposta ritardata. Tuttavia, c’è una riduzione sostanziale che può essere attribuita a una regolazione del dosaggio o a una sinergia farmacologica.</li><li><strong>Quarta Terapia: Methylprednisolone</strong><br>Introdotta al giorno 120, questa terapia mostra un profilo di rischio relativamente stabile. Piccole fluttuazioni indicano una discreta stabilità terapeutica, ma richiedono uno stretto monitoraggio per prevenire possibili incrementi.</li><li><strong>Quinta Terapia: Methotrexate</strong><br>Con l’introduzione di Methotrexate dal giorno 165, il rischio si mantiene su livelli bassi, suggerendo una buona efficacia a lungo termine. Questo potrebbe indicare che Methotrexate fornisca un controllo adeguato delle risposte autoimmuni in atto.</li></ul><h3>Consulenze e Suggerimenti</h3><p>La progressione delle terapie evidenzia l\'importanza di un approccio dinamico nella gestione del NMOSD. Mentre le terapie iniziali hanno abbassato drammaticamente il rischio, le fasi successive hanno mostrato oscillazioni che necessitano di attenzione clinica. È fondamentale regolare le dosi e considerare terapie combinate basate su risposte individuali. Continui test diagnostici e biomarcatori potrebbero affinare ulteriormente le scelte terapeutiche.</p><p>L’implementazione di un monitoraggio rigoroso e personalizzato sarà cruciale per ridurre il rischio di ricaduta e migliorare la qualità della vita del paziente.</p></div>';

  evaluation_sessions_by_row = 5;
  private evaluation_sessions: any[] = [];
  evaluation_sessions_row_1 = [];
  evaluation_sessions_row_2 = [];

  feedbackData: [] = [];
  criteriaData: [] = [];
  analysisType = 0;

  displayedColumnsCriteria: string[] = ['criteriatitle', 'suggest', 'vote', 'risk', 'contradiction'];
  displayedColumns: string[] = ['expert', 'answers', 'time', 'cost', 'total_cost', 'evaluation_session', 'new_proposal', 'evidence'];

  scoreColumns = [
    {key: 'ism', label: 'Information and Security Mgmt'},
    {key: 'dpp', label: 'Data Protection and Privacy'},
    {key: 'bcdr', label: 'Business Continuity and Disaster Recovery'},
    {key: 'acidm', label: 'Access Control & Identity Management'},
    {key: 'irsm', label: 'Incident Resp & Security Monitoring'},
    {key: 'tpscs', label: 'Third Party & Supply Chain Security'}
  ];
  nestedDisplayedColumns: string[] = [
    ...this.scoreColumns.map((col) => col.key),
    'overall_score',
    'ai_analysis_suggestion'
  ];
  expandedElement: any | null = null;
  tmp = null;

  statusColors: Record<string, string> = {
    Conformant: '#3ba272',
    'Need Revision': '#fac858',
    'Not Conformant': '#ee6666'
  };

  ngOnInit(): void {

    //  controllo se sono in modifica o in creazione
    this._activeRouteSubscription = this.activeRoute.params.subscribe(routeParams => {
      this.id = routeParams.id_decision;

      //  carico i grafici
      this.loaderService.loadChartPackages('corechart').subscribe(() => {
        this.init();
      });
    });
  }

  virtualAssistantLoading() {
    this.virtualAssistantStatus = 'pending';
    setTimeout(() => {
      this.virtualAssistantStatus = 'ready';
    }, 3000);
  }

  initLineChart(): void {
    const chartDom = document.getElementById('line-chart');
    const myChart = (window as any).echarts.init(chartDom);

    const option = {
      title: {
        text: 'Risk of Relapse Over Time',
        subtext: 'NMOSD Clinical Case',
      },
      tooltip: {
        trigger: 'axis',
        axisPointer: {
          type: 'cross'
        }
      },
      toolbox: {
        show: true,
        feature: {
          saveAsImage: {}
        }
      },
      xAxis: {
        type: 'category',
        boundaryGap: false,
        data: [
          'Day 1', 'Day 5', 'Day 10', 'Day 15', 'Day 20', 'Day 25', 'Day 30',
          'Day 35', 'Day 40', 'Day 45', 'Day 50', 'Day 55', 'Day 60', 'Day 65',
          'Day 70', 'Day 75', 'Day 80', 'Day 85', 'Day 90', 'Day 95', 'Day 100',
          'Day 105', 'Day 110', 'Day 115', 'Day 120', 'Day 125', 'Day 130',
          'Day 135', 'Day 140', 'Day 145', 'Day 150', 'Day 155', 'Day 160',
          'Day 165', 'Day 170', 'Day 175', 'Day 180', 'Day 185', 'Day 190',
          'Day 195', 'Day 200'
        ]
      },
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: '{value} %'
        },
        axisPointer: {
          snap: true
        }
      },
      visualMap: {
        show: false,
        dimension: 1, // Imposta il dimensione sull'asse Y
        pieces: [
          {gte: 0, lte: 33.33, color: 'green'},   // Low relapse risk
          {gt: 33.33, lte: 66.66, color: 'yellow'},  // Moderate relapse risk
          {gt: 66.66, lte: 100, color: 'red'}       // High relapse risk
        ],
        outOfRange: {
          color: ['red']  // Colore per valori al di fuori delle fasce specificate
        }
      },
      series: [
        {
          name: 'Relapse Risk',
          type: 'line',
          smooth: true,
          data: [
            85, 80, 75, 70, 60, 50, 30, 20, 25, 30, 50, 55, 70, 65, 60,
            40, 30, 20, 15, 10, 15, 20, 25, 20, 15, 10, 15, 20, 18, 16,
            14, 13, 15, 10, 8, 6, 5, 4, 3, 2
          ],
          markArea: {
            itemStyle: {
              color: 'rgba(173, 216, 230, 0.4)'
            },
            data: [
              [
                {
                  name: 'First Therapy\nRituximab',
                  xAxis: 'Day 5'
                },
                {
                  xAxis: 'Day 30'
                }
              ],
              [
                {
                  name: 'Second Therapy\nAzathioprine',
                  xAxis: 'Day 45'
                },
                {
                  xAxis: 'Day 70'
                }
              ],
              [
                {
                  name: 'Third Therapy\nMycophenolate Mofetil',
                  xAxis: 'Day 100'
                },
                {
                  xAxis: 'Day 110'
                }
              ],
              [
                {
                  name: 'Fourth Therapy\nMethylprednisolone',
                  xAxis: 'Day 140'
                },
                {
                  xAxis: 'Day 150'
                }
              ],
              [
                {
                  name: 'Fifth Therapy\nMethotrexate',
                  xAxis: 'Day 170'
                },
                {
                  xAxis: 'Day 180'
                }
              ]
            ]
          }
        }
      ],
      grid: {
        top: '20%',  // Aggiungi spazio sopra il grafico
        bottom: '10%'  // Aggiungi spazio sotto il grafico se necessario
      }
    };


    myChart.setOption(option);
  }

  ngOnDestroy() {
    this.unsubscribeAll();
  }

  unsubscribeAll() {
    if (this._activeRouteSubscription) {
      this._activeRouteSubscription.unsubscribe();
    }
  }


  init() {

    //  TODO_MICHELE: versione demo per Enzo
    if (environment.demo) {

      switch (this.id) {
        case '1':
          this.evaluation_sessions = this.demoService.casoSarcomatoidDashboard.evaluation_sessions.slice();
          this.feedbackData = this.demoService.casoSarcomatoidDashboard.feedbackData.slice();
          this.criteriaData = this.demoService.casoSarcomatoidDashboard.criteriaData.slice();
          this.analysisType = this.demoService.casoSarcomatoidDashboard.analysis_type;
          break;
        case '2':
          this.evaluation_sessions = this.demoService.casoSarcomatoidDashboard.evaluation_sessions.slice();
          this.feedbackData = this.demoService.casoSarcomatoidDashboard.feedbackData.slice();
          this.criteriaData = this.demoService.casoSarcomatoidDashboard.criteriaData.slice();
          this.analysisType = this.demoService.casoSarcomatoidDashboard.analysis_type;
          setTimeout(() => {
            this.drawChartTargets();
            this.drawChartWeightTargets();
          }, 500);
          break;
        case '4':
          const modelDemo = this.demoService.casoAirportCybersecurityDashboard;
          this.evaluation_sessions = modelDemo.evaluation_sessions.slice();
          this.feedbackData = modelDemo.feedbackData.slice();
          this.criteriaData = modelDemo.criteriaData.slice();
          this.analysisType = modelDemo.analysis_type;
          this.displayedColumns = [
            'select',
            'name',
            'status'
          ];
          setTimeout(() => {
            this.demo3DrawChartTargets(this.feedbackData?.filter((item: any) => item.selected));
            this.demo3DrawChartWeightTargets();
          }, 400);
          break;

        default:
          break;
      }

      this.initLineChart();
    }

    this.evaluation_sessions_row_1 = this.evaluation_sessions.splice(0, this.evaluation_sessions_by_row);
    this.evaluation_sessions_row_2 = this.evaluation_sessions;
  }


  /** Disegna i grafici delle proposte */
  drawChartTargets() {

    const data = google.visualization.arrayToDataTable([
      ['Title', 'Cost', 'Delivery', 'Quality', 'Risk', {role: 'annotation'}],
      ['No Modularization', {v: 82.9, f: 82.9 + '%'}, {v: 129.77, f: 129.77 + '%'}, {v: 84.87, f: 84.87 + '%'}, {
        v: 107.89,
        f: 107.89 + '%'
      }, ''],
      ['Modularization', {v: 167.53, f: 167.53 + '%'}, {v: 252.38, f: 252.38 + '%'}, {v: 164.94, f: 164.94 + '%'}, {
        v: 223.9,
        f: 223.9 + '%'
      }, ''],
    ]);

    const view = new google.visualization.DataView(data);

    const chart = new google.visualization.BarChart(document.getElementById('chart_div_target'));

    chart.draw(view, {
      height: 400,
      legend: {position: 'top', maxLines: 3},
      bar: {groupWidth: '75%'},
      isStacked: true
    });
  }

  /** Disegna i grafici dei pesi degli obiettivi */
  drawChartWeightTargets() {

    const data = google.visualization.arrayToDataTable([['Task', 'Hours per Day'],
      ['Risk', 27],
      ['Delivery', 31],
      ['Cost', 21],
      ['Quality', 21]
    ]);

    const view = new google.visualization.DataView(data);

    const chart = new google.visualization.PieChart(document.getElementById('chart_div_weight_target'));

    chart.draw(view, {
      height: 400,
      legend: {position: 'top', maxLines: 3}
    });
  }

  createGoogleChartData(feedbackData) {
    if (!feedbackData || feedbackData.length === 0) {
      return null; // Gestisce il caso di dati mancanti
    }

    // Estrae i nomi degli slug (es. 'ism', 'dpp', ecc.) dalla prima riga
    const slugs = Object.keys(feedbackData[0].scores);

    // 1. Costruisci l'intestazione (Header)
    // Utilizza i 'title' (nomi completi) degli score per l'header del grafico,
    // ad eccezione della prima colonna 'Title' e dell'ultima '{role: 'annotation'}'

    // Esempio: ['Title', 'Information and Security Management', 'Data Protection and Privacy', ..., {role: 'annotation'}]
    const header = [
      'Title',
      ...slugs.map(slug => feedbackData[0].scores[slug].title),
      {role: 'annotation'}
    ];

    // 2. Costruisci le righe di dati
    const rows = feedbackData.map(item => {
      const row = [item.name]; // Inizia con il 'Title' (nome ALFA, BETA, etc.)

      // Itera sugli slug per recuperare i valori in ordine
      slugs.forEach(slug => {
        const scoreObject = item.scores[slug];
        const value = scoreObject.value;

        // Aggiunge l'oggetto Google Charts {v: value, f: formatted_value}
        row.push({
          v: value,
          f: value.toFixed(2) + '%' // Formatta il valore numerico in percentuale
        });
      });

      // Aggiunge l'annotazione vuota
      row.push('');

      return row;
    });

    // 3. Combina l'header e le righe per l'output finale
    return [header, ...rows];
  }

  toggleRow(row: any): void {
    this.expandedElement = this.expandedElement === row ? null : row;
  }

  getOverallScore(row: any): number {
    const s = row.scores;
    return s.ism.value + s.dpp.value + s.bcdr.value + s.acidm.value + s.irsm.value + s.tpscs.value;
  }

  onCompanySelect(row: any): void {
    row.selected = !row.selected;

    // @ts-ignore
    const selectedCompanies = this.feedbackData.filter(item => item.selected);

    this.demo3DrawChartTargets(selectedCompanies);
  }

  /** Disegna i grafici delle proposte */
  demo3DrawChartTargets(filteredData: any[]) {
    const chartArray = this.createGoogleChartData(filteredData);
    const chartDom = document.getElementById('chart_div_target');
    const myChart = (window as any).echarts.init(chartDom);

    const yAxisData = chartArray.slice(1).map(row => row[0]);

    const series = chartArray[0].slice(1, -1).map((category, colIndex) => {
      return {
        name: category,
        type: 'bar',
        stack: 'total',
        label: {
          show: true,
          formatter: '{c}%' // aggiunge il simbolo % ai valori delle barre
        },
        emphasis: {focus: 'series'},
        data: chartArray.slice(1).map(row => row[colIndex + 1].v)
      };
    });

    const option = {
      tooltip: {
        trigger: 'axis',
        axisPointer: {type: 'shadow'},
        formatter: params => {
          // mostra valori in percentuale anche nel tooltip
          return params.map(p => `${p.seriesName}: ${p.value}%`).join('<br/>');
        }
      },
      legend: {},
      xAxis: {
        type: 'value',
        axisLabel: {
          formatter: '{value}%' // aggiunge il simbolo % all'asse X
        }
      },
      yAxis: {type: 'category', data: yAxisData},
      series
    };

    myChart.setOption(option);
  }

  /** Disegna i grafici dei pesi degli obiettivi */
  demo3DrawChartWeightTargets() {
    const div = document.getElementById('chart_div_weight_target');
    if (!div) {
      return;
    }

    const myChart = (window as any).echarts.init(div);

    // Conta le compagnie per ciascuno status
    const counts = this.feedbackData.reduce(
      (acc, item) => {
        // @ts-ignore
        if (item.status === 'Conformant') {
          acc.conformant += 1;
        } else {
          // @ts-ignore
          if (item.status === 'Need Revision') {
            acc.needRevision += 1;
          }
          // @ts-ignore
          else if (item.status === 'Not Conformant') {
            acc.notConformant += 1;
          }
        }
        return acc;
      },
      {conformant: 0, needRevision: 0, notConformant: 0}
    );

    const option = {
      title: {
        text: 'Questionnaire Compliance Distribution',
        left: 'center'
      },
      tooltip: {
        trigger: 'item',
        formatter: '{b}: {c} ({d}%)'
      },
      legend: {
        orient: 'vertical',
        left: 'left',
        data: ['Conformant', 'Need Revision', 'Not Conformant']
      },
      series: [
        {
          name: 'Company Status',
          type: 'pie',
          radius: '50%',
          data: [
            {value: counts.conformant, name: 'Conformant', itemStyle: {color: '#3ba272'}},
            {value: counts.needRevision, name: 'Need Revision', itemStyle: {color: '#fac858'}},
            {value: counts.notConformant, name: 'Not Conformant', itemStyle: {color: '#ee6666'}}
          ],
          emphasis: {
            itemStyle: {
              shadowBlur: 10,
              shadowOffsetX: 0,
              shadowColor: 'rgba(0, 0, 0, 0.5)'
            }
          },
          label: {
            formatter: '{b}: {c} ({d}%)'
          }
        }
      ]
    };

    myChart.setOption(option);
  }

  getStatusColor(status: string): string {
    return this.statusColors[status] || '#ccc'; // colore di default se non c'è corrispondenza
  }

}
